import type { RoutesPlannerRun, RoutesPlannerTask } from '../../types';
import type { RoutesPlannerBreak } from '../../components/routes-planner/routes-planner';
import { isTaskAssigned } from '../../components/routes-planner/utils';
import { updateRunPriorities } from '../../stores/helpers/update-tasks';
import type { EtaResponseRouteItem, RunChangedParams } from '@bringg-frontend/eta-calculator';
import { NO_ROUTE_ID } from '../../stores/routes-planner-store';
import { EtaResponseType } from '@bringg-frontend/eta-calculator';

function routesToRunsChangedParams(
	runIds: number[],
	runToTasks: { [runId: number]: RoutesPlannerTask[] },
	runToBreaks: { [runId: number]: RoutesPlannerBreak[] },
	routesById: { [runId: number]: RoutesPlannerRun }
) {
	return runIds
		.filter(runId => isTaskAssigned(runId))
		.reduce((dataToCalculate, runId) => {
			const tasks = runToTasks[runId];
			if (!tasks) {
				return dataToCalculate;
			}

			const [sortedTasks, sortedBreaks] = updateRunPriorities(tasks, runToBreaks[runId]);

			dataToCalculate.push(routeToRunChangedParams(routesById[runId], sortedTasks, sortedBreaks));

			return dataToCalculate;
		}, [] as RunChangedParams[]);
}

function routeToRunChangedParams(
	route: RoutesPlannerRun | EtaResponseRouteItem,
	tasks: RoutesPlannerTask[],
	driverBreaks: RoutesPlannerBreak[]
): RunChangedParams {
	const trailerId = tasks?.[0]?.vehicle_id;
	const vehicleId = route
		? (route as EtaResponseRouteItem).power_unit_id || (route as RoutesPlannerRun).vehicle_id
		: undefined;

	return {
		runStartTime: route?.depot_etl,
		runEndTime: route?.depot_back_eta,
		driverId: route?.user_id,
		vehicleId,
		trailerId: vehicleId !== trailerId ? trailerId : undefined,
		runOrderedTasks: tasks,
		driverBreaks,
		runId: route ? getRouteId(route) : NO_ROUTE_ID,
		routeIndex: undefined
	};
}

export const routeToEtaResponseItem = (route: RoutesPlannerRun): EtaResponseRouteItem => {
	const { depot_etl, depot_back_eta, route_idx, transport_distance, user_id, vehicle_id, id, transport_time } = route;
	return {
		depot_etl,
		depot_back_eta,
		route_idx,
		transport_distance,
		user_id,
		type: EtaResponseType.RouteInfo,
		power_unit_id: vehicle_id,
		run_id: id,
		transport_time
	};
};

function getRouteId(route: EtaResponseRouteItem | RoutesPlannerRun) {
	return (route as EtaResponseRouteItem).run_id ?? (route as RoutesPlannerRun).id;
}

export const etaRoutesConverter = {
	routesToRunsChangedParams,
	routeToRunChangedParams,
	routeToEtaResponseItem,
	getRouteId
};
